import { useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';

/**
 * Whether two buyer payloads are the same save. The one definition both the saved state below and
 * usePaymentSection's in-flight check use, so the two cannot disagree on what "unchanged" means.
 * @param {object|null} savedValues - The payload a save sent.
 * @param {object} currentValues - The buyer form values as of now.
 * @returns {boolean} True when saving `currentValues` would send exactly `savedValues`.
 */
export const purchaserValuesMatch = (savedValues, currentValues) =>
  savedValues !== null && isEqual(savedValues, currentValues);

/**
 * usePurchaserSaveState hook.
 * Track whether the buyer form still holds what Quick Purchase last saved to the purchase.
 *
 * PRO2-146: the buyer counts as saved only while the form equals the payload of the last save that
 * succeeded, not merely once any save has happened. An edit made after a save — a RUC typed after
 * choosing PayPal, an email corrected after a declined payment — makes the buyer unsaved again, so
 * the next payment attempt saves first instead of charging against the earlier buyer data.
 *
 * Both sides come from the same `purchaserFormValues` allowlist, and a save never rewrites the form,
 * so the comparison converges right after every save by construction. It records what this page
 * sent, not what the backend holds.
 * @param {object} purchaserFormValues - The buyer form values Quick Purchase sends when it saves.
 * @returns {{ purchaserSubmitted: boolean, markPurchaserSaved: Function }} Whether the buyer is
 * saved and unchanged since, and the callback that records a successful save with what it sent.
 */
function usePurchaserSaveState(purchaserFormValues) {
  const [savedPurchaserValues, setSavedPurchaserValues] = useState(null);

  // Not memoized: `purchaserFormValues` is a multi-field form selection, a new object every render,
  // so a memo would never hit. The comparison is a handful of scalars and one address.
  const purchaserSubmitted = purchaserValuesMatch(savedPurchaserValues, purchaserFormValues);

  // State, not a ref: recording a save must re-render the owner (see the note where QuickCheckout
  // calls this hook).
  const markPurchaserSaved = useCallback((sentValues) => setSavedPurchaserValues(sentValues), []);

  return { purchaserSubmitted, markPurchaserSaved };
}

export default usePurchaserSaveState;
